import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/* ======================================================
   CONFIGURACIÓN
   ====================================================== */
const COMPLEMENTS_COLLECTION = 'complementos-hasta-25';
const COMPLEMENT_TYPE = 'Complemento';
const INSURANCE_TYPE = 'Seguro';
const INSURANCE_HANDLE = 'envio-protegido';
const MAX_PRODUCTS = 8;

/* Memoria del navegador para que el carrusel salga al instante */
const CATALOG_CACHE_KEY = 'lumira:complements:v1';
const CATALOG_CACHE_TTL = 10 * 60 * 1000;
const CATALOG_REVALIDATE_AFTER = 2 * 60 * 1000;
const THUMB_WIDTH = 160;

/* El seguro se reconoce por su tipo de producto o por su dirección. */
function isInsurance(item) {
  return item.product_type === INSURANCE_TYPE || item.handle === INSURANCE_HANDLE;
}

/* ======================================================
   AÑADIR AL CARRITO (sistema de eventos de Horizon)
   ====================================================== */
async function addLumiraToCart({
  variantId,
  quantity = 1,
  sourceElement,
  productId = ''
}) {
  if (!variantId) {
    throw new Error('No se ha encontrado la variante.');
  }

  const sectionIds = [];

  document
    .querySelectorAll('cart-items-component[data-section-id]')
    .forEach((component) => {
      const sectionId = component.dataset.sectionId;

      if (sectionId && !sectionIds.includes(sectionId)) {
        sectionIds.push(sectionId);
      }
    });

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  sourceElement.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [
        {
          merchandiseId: String(variantId),
          quantity: Number(quantity)
        }
      ],
      promise: deferredEventPromise.promise
    })
  );

  try {
    const payload = {
      items: [
        {
          id: Number(variantId),
          quantity: Number(quantity)
        }
      ],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname
    };

    const response = await fetch(`${getLumiraRoot()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status) {
      const error = new Error(
        responseData.description ||
        responseData.message ||
        'No se pudo añadir el producto.'
      );

      deferredEventPromise.reject(error);

      throw error;
    }

    const cart = await getLumiraCart();

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'lumira-cart-upsell',
        sourceId: 'lumira-cart-upsell',
        itemCount: Number(quantity),
        productId: String(productId || ''),
        sections: responseData.sections,
        didError: false
      }
    });

    return cart;
  } catch (error) {
    try {
      deferredEventPromise.reject(error);
    } catch (_) {
      // Ya resuelta/rechazada.
    }

    throw error;
  }
}

/* ======================================================
   ESTADO Y UTILIDADES
   ====================================================== */
const SLOT = '#cart-drawer [data-cart-recommendations-slot]';
const ITEMS = '#cart-drawer .lumira-cart-scroll-area .cart-drawer__items';
const jsonCache = new Map();
let generation = 0;
let pendingFrame = null;
let observedDrawer = null;
let observer = null;
let adding = false;
let state = null; // { signature, currency, items, index, selected }
let cleaning = false;

function getLumiraRoot() {
  return window.Shopify?.routes?.root || '/';
}

function escapeHtml(value = '') {
  return String(value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[c]));
}

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function money(cents, currency) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(Number(cents) / 100);
}

function toCents(value) {
  if (value === null || value === undefined || value === '') return 0;
  return Math.round(parseFloat(value) * 100) || 0;
}

/* Foto pequeña: la miniatura mide 62 px, no hace falta la original. */
function thumb(src, width = THUMB_WIDTH) {
  if (!src) return '';

  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (error) {
    return src;
  }
}

async function requestJson(url) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`No se pudo cargar (${response.status}).`);
  }

  return response.json();
}

function cachedJson(url) {
  const existing = jsonCache.get(url);

  if (existing && Date.now() - existing.time < 60000) return existing.promise;

  const promise = requestJson(url).catch((error) => {
    jsonCache.delete(url);
    throw error;
  });

  jsonCache.set(url, { time: Date.now(), promise });

  return promise;
}

async function getLumiraCart() {
  return requestJson(`${getLumiraRoot()}cart.js`);
}

/* ======================================================
   COMPLEMENTOS: DATOS
   ====================================================== */
const ACCESSORY_WORDS = /\b(pendientes|pendiente|earrings|earring|pulsera|pulseras|bracelet|bracelets|collar|collares|necklace|necklaces|bolso|bolsos|bag|bags|cinturon|cinturones|belt|belts|panuelo|panuelos|scarf|scarves|broche|broches|anillo|anillos|ring|rings|gafas|sombrero|accesorios|accesorio|accessories|complementos|complemento|joya|joyas|joyeria|bisuteria|jewelry|jewellery)\b/;
const CLOTHING_WORDS = /\b(vestido|vestidos|dress|dresses|camisa|blusa|pantalon|falda|abrigo|chaqueta|jersey|zapatos|sandalias|botas|conjunto|conjuntos|mono|camiseta)\b/;

/* Un complemento es: tipo "Complemento", etiqueta "complemento", o un accesorio por su nombre. */
function isComplement(raw) {
  const type = normalizeText(raw.product_type).trim();
  const tags = normalizeText(Array.isArray(raw.tags) ? raw.tags.join(' ') : raw.tags);
  const text = normalizeText(`${raw.product_type || ''} ${raw.title || ''}`);

  if (type === normalizeText(COMPLEMENT_TYPE)) return true;
  if (/(^|[ ,])(lumira-upsell|lumira-complemento|complemento)([ ,]|$)/.test(tags)) return true;
  if (CLOTHING_WORDS.test(text)) return false;

  return ACCESSORY_WORDS.test(text);
}

function normalizeProduct(raw, maxPrice) {
  const variants = (raw.variants || [])
    .map((v) => ({
      id: v.id,
      options: [v.option1, v.option2, v.option3].filter((x) => x !== null && x !== undefined),
      price: toCents(v.price),
      compare: toCents(v.compare_at_price),
      available: Boolean(v.available),
      image: v.featured_image?.src || ''
    }))
    .filter((v) => v.available && v.price > 0 && v.price <= maxPrice);

  if (!variants.length) return null;

  const options = (raw.options || []).map((option, index) => {
    const values = [];

    variants.forEach((v) => {
      const value = v.options[index];
      if (value !== undefined && !values.includes(value)) values.push(value);
    });

    return {
      name: typeof option === 'string' ? option : option.name,
      values
    };
  });

  return {
    id: raw.id,
    title: raw.title,
    handle: raw.handle,
    options,
    variants,
    image: raw.images?.[0]?.src || raw.image?.src || ''
  };
}

async function fetchProductsJson(url) {
  try {
    const data = await cachedJson(url);
    return data.products || [];
  } catch (error) {
    return [];
  }
}

/* Versión ligera de cada producto: lo único que se guarda en el navegador. */
function slimProduct(raw) {
  return {
    id: raw.id,
    title: raw.title,
    handle: raw.handle,
    product_type: raw.product_type,
    tags: raw.tags,
    options: (raw.options || []).map((option) => (typeof option === 'string' ? option : option.name)),
    variants: (raw.variants || []).map((v) => ({
      id: v.id,
      option1: v.option1,
      option2: v.option2,
      option3: v.option3,
      price: v.price,
      compare_at_price: v.compare_at_price,
      available: v.available,
      featured_image: v.featured_image ? { src: v.featured_image.src } : null
    })),
    images: raw.images?.[0] ? [{ src: raw.images[0].src }] : [],
    image: raw.image ? { src: raw.image.src } : null
  };
}

function readCachedCatalog() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(CATALOG_CACHE_KEY));

    if (
      stored &&
      Array.isArray(stored.products) &&
      stored.products.length &&
      Date.now() - stored.time < CATALOG_CACHE_TTL
    ) {
      return stored;
    }
  } catch (error) {
    // Sin memoria del navegador: se pide a la tienda como siempre.
  }

  return null;
}

function writeCachedCatalog(products) {
  try {
    sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({ time: Date.now(), products }));
  } catch (error) {
    // Sin memoria del navegador: no pasa nada.
  }
}

async function fetchCatalog() {
  const root = getLumiraRoot();

  let products = await fetchProductsJson(
    `${root}collections/${encodeURIComponent(COMPLEMENTS_COLLECTION)}/products.json?limit=50`
  );

  if (!products.length) {
    // Sin colección: se buscan en toda la tienda.
    const all = await fetchProductsJson(`${root}collections/all/products.json?limit=250`);
    products = all.filter(isComplement);
  }

  const slim = products.map(slimProduct);

  if (slim.length) writeCachedCatalog(slim);

  return slim;
}

let catalogPromise = null;
let catalogRevalidating = false;

function getCatalog() {
  const cached = readCachedCatalog();

  if (cached) {
    if (Date.now() - cached.time > CATALOG_REVALIDATE_AFTER && !catalogRevalidating) {
      catalogRevalidating = true;
      fetchCatalog()
        .catch(() => {})
        .finally(() => { catalogRevalidating = false; });
    }

    return Promise.resolve(cached.products);
  }

  if (!catalogPromise) {
    catalogPromise = fetchCatalog().finally(() => { catalogPromise = null; });
  }

  return catalogPromise;
}

function buildItems(products, inCartIds, maxPrice) {
  const inCart = new Set(inCartIds.map(String));

  return products
    .filter((raw) => String(raw.product_type || '') !== INSURANCE_TYPE && raw.handle !== INSURANCE_HANDLE)
    .filter((raw) => !inCart.has(String(raw.id)))
    .map((raw) => normalizeProduct(raw, maxPrice))
    .filter(Boolean)
    .slice(0, MAX_PRODUCTS);
}

async function loadComplements(inCartIds, maxPrice) {
  const products = await getCatalog();
  const result = buildItems(products, inCartIds, maxPrice);

  if (!result.length) {
    console.warn('[Lumira complemento] No hay complementos disponibles: accesorios activos, con stock, de menos de 25 € y con tipo "Complemento" o nombre de accesorio (pendientes, collar, pulsera…).');
  }

  return result;
}

function currentVariant(product) {
  const selectedId = state?.selected?.[product.id];
  const found = product.variants.find((v) => String(v.id) === String(selectedId));

  if (found) return found;

  const withCover = product.variants.find((v) => v.image && v.image === product.image);

  return withCover || product.variants[0];
}

/* ======================================================
   COMPLEMENTOS: PINTAR
   ====================================================== */
function clearSlot(slot) {
  if (!slot) return;
  if (slot.firstChild) slot.replaceChildren();
  if (!slot.hidden) slot.hidden = true;
}

function render(slot) {
  if (!slot) return;

  if (!state || !state.items.length) {
    clearSlot(slot);
    updateMoreHint();
    return;
  }

  const product = state.items[state.index];
  const variant = currentVariant(product);
  const currency = state.currency;
  const discounted = variant.compare > variant.price;
  const percent = discounted
    ? Math.floor(((variant.compare - variant.price) * 100) / variant.compare)
    : 0;
  const image = thumb(variant.image || product.image);
  const url = `${getLumiraRoot()}products/${encodeURIComponent(product.handle)}`;
  const multiple = state.items.length > 1;

  const selects = product.options
    .map((option, index) => {
      if (option.values.length < 2) return '';

      const optionsHtml = option.values
        .map((value) => {
          const selected = variant.options[index] === value ? ' selected' : '';
          return `<option value="${escapeHtml(value)}"${selected}>${escapeHtml(value)}</option>`;
        })
        .join('');

      return `<select class="lcu__select" data-lcu-option="${index}" aria-label="${escapeHtml(option.name)}">${optionsHtml}</select>`;
    })
    .join('');

  const chevronLeft = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>';
  const chevronRight = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';

  const dots = multiple
    ? `<div class="lcu__dots" aria-hidden="true">${state.items
        .map((_, i) => `<span class="lcu__dot${i === state.index ? ' is-active' : ''}"></span>`)
        .join('')}</div>`
    : '';

  slot.innerHTML = `
    <section class="lcu" data-lumira-cart-upsell aria-label="Complementos recomendados">
           <h3 class="lcu__title">El 83% de las clientas también se llevó:</h3>

      <div class="lcu__row">
        ${multiple ? `<button class="lcu__arrow" type="button" data-lcu-prev aria-label="Complemento anterior">${chevronLeft}</button>` : ''}
        <article class="lcu__card" data-product-id="${escapeHtml(product.id)}">
          <a class="lcu__media" href="${escapeHtml(url)}" tabindex="-1" aria-hidden="true">
            ${image ? `<img class="lcu__image" src="${escapeHtml(image)}" alt="" width="64" height="85" loading="eager">` : ''}
            ${percent ? `<span class="lcu__badge">-${percent}%</span>` : ''}
          </a>
          <div class="lcu__body">
            <a class="lcu__name" href="${escapeHtml(url)}">${escapeHtml(product.title)}</a>
            <div class="lcu__prices">
              <strong class="lcu__price">${escapeHtml(money(variant.price, currency))}</strong>
              ${discounted ? `<s class="lcu__compare">${escapeHtml(money(variant.compare, currency))}</s>` : ''}
            </div>
            <div class="lcu__actions">
              ${selects}
              <button class="lcu__add" type="button" data-lumira-cart-add data-variant-id="${escapeHtml(variant.id)}" aria-label="Añadir ${escapeHtml(product.title)}">Añadir</button>
            </div>
          </div>
        </article>
        ${multiple ? `<button class="lcu__arrow" type="button" data-lcu-next aria-label="Complemento siguiente">${chevronRight}</button>` : ''}
      </div>
      ${dots}
      <p class="lcu__error" role="status" hidden></p>
    </section>`;

  slot.hidden = false;
  updateMoreHint();
}

function restore() {
  const drawer = document.querySelector('#cart-drawer');

  if (drawer?.querySelector('.cart-drawer--empty')) {
    state = null;
    return;
  }

  const slot = document.querySelector(SLOT);

  if (slot && state && !slot.querySelector('[data-lumira-cart-upsell]')) render(slot);

  updateMoreHint();
}

function move(delta) {
  if (!state || state.items.length < 2) return;

  state.index = (state.index + delta + state.items.length) % state.items.length;
  render(document.querySelector(SLOT));
}

function changeOption(select) {
  if (!state) return;

  const product = state.items[state.index];
  const slot = document.querySelector(SLOT);
  const chosen = product.options.map((option, index) => {
    const el = slot.querySelector(`[data-lcu-option="${index}"]`);
    return el ? el.value : option.values[0];
  });

  const changedIndex = Number(select.dataset.lcuOption);

  let variant = product.variants.find((v) => v.options.every((value, i) => value === chosen[i]));

  if (!variant) {
    variant = product.variants.find((v) => v.options[changedIndex] === chosen[changedIndex]);
  }

  if (variant) state.selected[product.id] = variant.id;

  render(slot);
}

/* ======================================================
   AVISO "VER MÁS PRODUCTOS"
   Aparece cuando la lista de productos se puede desplazar
   y todavía hay productos por debajo.
   ====================================================== */
function updateMoreHint() {
  const container = document.querySelector(ITEMS);
  const hint = container?.querySelector('[data-lumira-more-hint]');

  if (!container || !hint) return;

  const remaining = container.scrollHeight - container.clientHeight - container.scrollTop;
  const scrollable = container.scrollHeight > container.clientHeight + 8;

  hint.hidden = !(scrollable && remaining > 24);
}

function scheduleHintUpdate() {
  requestAnimationFrame(updateMoreHint);
}

/* ======================================================
   ACTUALIZAR RECOMENDACIÓN SEGÚN EL CARRITO
   ====================================================== */

/* Pintado inmediato con los datos que ya hay en la página y en la memoria del navegador. */
function quickRender(slot) {
  if (state || !slot) return;

  const cached = readCachedCatalog();

  if (!cached) return;

  const ids = (slot.dataset.cartProductIds || '').split(',').filter(Boolean);

  if (!ids.length) return;

  const currency = window.Shopify?.currency?.active || 'EUR';
  const maxPrice = Number(slot.dataset.maxPrice) || 2500;
  const items = buildItems(cached.products, ids, maxPrice);

  if (!items.length) return;

  state = {
    signature: `${currency}:${maxPrice}:${[...new Set(ids)].sort().join(',')}`,
    currency,
    items,
    index: 0,
    selected: {}
  };

  render(slot);
}

async function refresh() {
  restore();

  const token = ++generation;
  const slot = document.querySelector(SLOT);

  if (!slot || adding) return;

  quickRender(slot);

  try {
    const cart = await getLumiraCart();

    if (token !== generation || !slot.isConnected || adding) return;

    const realItems = cart.items.filter((item) => !isInsurance(item));

    if (!realItems.length) {
      state = null;
      clearSlot(slot);
      cleanupInsurance();
      return;
    }

    const currency = cart.currency || window.Shopify?.currency?.active || 'EUR';
    const maxPrice = Number(slot.dataset.maxPrice) || 2500;
    const ids = [...new Set(realItems.map((item) => String(item.product_id)))].sort();
    const signature = `${currency}:${maxPrice}:${ids.join(',')}`;

    if (state && state.signature === signature) {
      restore();
      return;
    }

    const items = await loadComplements(ids, maxPrice);

    if (token !== generation || !slot.isConnected || adding) return;

    const previousId = state?.items?.[state.index]?.id;
    const previousIndex = previousId ? items.findIndex((p) => p.id === previousId) : -1;

    state = {
      signature,
      currency,
      items,
      index: previousIndex >= 0 ? previousIndex : 0,
      selected: state?.selected || {}
    };

    render(slot);
  } catch (error) {
    console.warn('[Lumira complemento]', error);
  }
}

function scheduleRefresh() {
  restore();

  if (pendingFrame !== null) cancelAnimationFrame(pendingFrame);

  pendingFrame = requestAnimationFrame(() => {
    pendingFrame = null;
    refresh();
  });
}

/* ======================================================
   SEGURO: si solo queda el seguro, se quita solo
   ====================================================== */
function cleanupInsurance() {
  if (cleaning) return;

  const rows = [...document.querySelectorAll('#cart-drawer .cart-items__table-row')];
  const insuranceRows = rows.filter((row) => row.hasAttribute('data-lumira-insurance-line'));

  if (insuranceRows.length && insuranceRows.length === rows.length) {
    cleaning = true;
    insuranceRows[0].querySelector('.cart-items__remove')?.click();
    setTimeout(() => { cleaning = false; }, 1500);
  }
}

/* ======================================================
   INICIO
   ====================================================== */
function initialize() {
  const drawer = document.querySelector('#cart-drawer');

  if (drawer !== observedDrawer) {
    observer?.disconnect();
    observedDrawer = drawer;

    if (drawer) {
      observer = new MutationObserver((mutations) => {
        restore();

        const relevant = mutations.some((m) => {
          if (m.type === 'attributes') return m.target.matches('[data-cart-recommendations-slot]');

          return [...m.addedNodes].some((node) => node instanceof Element && (
            node.matches('[data-cart-recommendations-slot]') ||
            node.querySelector('[data-cart-recommendations-slot]')
          ));
        });

        if (relevant) scheduleRefresh();
      });

      observer.observe(drawer, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-cart-product-ids', 'data-product-id']
      });
    }
  }

  scheduleRefresh();
  scheduleHintUpdate();
}

/* ======================================================
   EVENTOS
   ====================================================== */
document.addEventListener('click', async (event) => {
  if (!(event.target instanceof Element)) return;

  /* Aviso "Ver más productos": baja la lista */
  if (event.target.closest('#cart-drawer [data-lumira-more-click]')) {
    event.preventDefault();

    const container = document.querySelector(ITEMS);

    container?.scrollBy({ top: container.clientHeight * 0.85, behavior: 'smooth' });

    return;
  }

  /* Flechas del carrusel */
  if (event.target.closest('#cart-drawer [data-lcu-prev]')) {
    move(-1);
    return;
  }

  if (event.target.closest('#cart-drawer [data-lcu-next]')) {
    move(1);
    return;
  }

  /* Seguro: activar / desactivar */
  const toggle = event.target.closest('#cart-drawer [data-lumira-insurance-toggle]');

  if (toggle) {
    event.preventDefault();

    if (toggle.dataset.busy === '1') return;

    const wasOn = toggle.getAttribute('aria-checked') === 'true';

    toggle.dataset.busy = '1';
    toggle.setAttribute('aria-busy', 'true');
    toggle.setAttribute('aria-checked', String(!wasOn));

    try {
      if (wasOn) {
        const remove = document.querySelector('#cart-drawer [data-lumira-insurance-line] .cart-items__remove');

        if (!remove) throw new Error('No se encontró el seguro en el carrito.');

        remove.click();
      } else {
        await addLumiraToCart({
          variantId: toggle.dataset.variantId,
          quantity: 1,
          sourceElement: toggle,
          productId: toggle.dataset.productId
        });
      }
    } catch (error) {
      console.warn('[Lumira seguro]', error);
      toggle.setAttribute('aria-checked', String(wasOn));
    } finally {
      delete toggle.dataset.busy;
      toggle.removeAttribute('aria-busy');
    }

    return;
  }

  /* Añadir complemento */
  const button = event.target.closest('#cart-drawer [data-lumira-cart-add]');

  if (!button || button.disabled || adding) return;

  event.preventDefault();

  adding = true;
  button.disabled = true;
  button.textContent = 'Añadiendo…';

  const errorBox = button.closest('[data-lumira-cart-upsell]')?.querySelector('.lcu__error');

  if (errorBox) errorBox.hidden = true;

  const productId = button.closest('[data-product-id]')?.dataset.productId || '';

  try {
    await addLumiraToCart({
      variantId: button.dataset.variantId,
      quantity: 1,
      sourceElement: button,
      productId
    });

    if (state) {
      state.items = state.items.filter((p) => String(p.id) !== String(productId));
      state.index = Math.min(state.index, Math.max(0, state.items.length - 1));
    }

    render(document.querySelector(SLOT));
  } catch (error) {
    const activeButton = document.querySelector(`${SLOT} [data-lumira-cart-add]`) || button;

    if (activeButton.isConnected) {
      activeButton.disabled = false;
      activeButton.textContent = 'Reintentar';

      const activeError = activeButton.closest('[data-lumira-cart-upsell]')?.querySelector('.lcu__error');

      if (activeError) {
        activeError.textContent = error.message || 'No se pudo añadir. Inténtalo de nuevo.';
        activeError.hidden = false;
      }
    }
  } finally {
    adding = false;
    jsonCache.clear();
    restore();
  }
});

document.addEventListener('change', (event) => {
  if (!(event.target instanceof Element)) return;

  const select = event.target.closest('#cart-drawer [data-lcu-option]');

  if (select) changeOption(select);
});

/* Los eventos de scroll no burbujean: se escuchan en fase de captura. */
document.addEventListener('scroll', (event) => {
  if (event.target instanceof Element && event.target.matches(ITEMS)) updateMoreHint();
}, true);

window.addEventListener('resize', scheduleHintUpdate);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(() => {
    scheduleRefresh();
    scheduleHintUpdate();
  }).catch(() => {});
});

document.addEventListener('shopify:section:load', initialize);
window.addEventListener('pageshow', initialize);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}

/* Descarga los complementos y TODAS sus fotos (portada y variantes) con el
   navegador en reposo, para que el carrusel salga al instante. */
function preloadImages(products) {
  const seen = new Set();

  buildItems(products, [], 2500).forEach((product) => {
    [product.image, ...product.variants.map((variant) => variant.image)].forEach((source) => {
      const src = thumb(source);

      if (!src || seen.has(src)) return;

      seen.add(src);

      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    });
  });
}

const whenIdle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1500));

whenIdle(() => {
  getCatalog()
    .then(preloadImages)
    .catch(() => {});
});


/* Evitar el marco de enfoque automático; conservar indicación para teclado. */
let keyboardNavigation = false;

function updateFocusMode() {
  document.querySelector('#cart-drawer')?.toggleAttribute('data-lumira-keyboard-focus', keyboardNavigation);
}

document.addEventListener('keydown', (event) => {
  if (event.key === 'Tab') {
    keyboardNavigation = true;
    updateFocusMode();
  }
}, true);

document.addEventListener('pointerdown', () => {
  keyboardNavigation = false;
  updateFocusMode();
}, true);

document.addEventListener('shopify:section:load', updateFocusMode);
